<template>
  <div>
    <div class="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-lg tw-p-4" role="alert">
      <div class="tw-flex tw-justify-between tw-items-center tw-gap-4">
        <div class="tw-space-y-1">
          <p class="tw-text-sm tw-text-gray-700 tw-mb-0">
            {{ t('payment.titles.payment_adjustment_message') }}
          </p>
          <p class="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-0">
            Total Amount: {{ n(+props.paymentAmount, 'currency') }}
          </p>
          <p class="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-0">
            Unallocated Amount: {{ n(+unallocatedAmount, 'currency') }}
          </p>
        </div>
        <button
          class="tw-bg-primary-500 tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-whitespace-nowrap hover:tw-bg-primary-600"
          @click="distributeEvenly"
          :disabled="!orderList.length"
        >
          {{ t('payment.titles.distribute_evenly') }}
        </button>
      </div>
    </div>

    <div class="tw-mt-4">
      <div v-if="loading" class="tw-flex tw-justify-center tw-py-8">
        <div
          class="tw-animate-spin tw-h-8 tw-w-8 tw-border-4 tw-border-primary-200 tw-border-t-primary-600 tw-rounded-full"
        ></div>
      </div>

      <div v-else class="tw-overflow-x-auto tw--mx-2 sm:tw--mx-4 tw-pb-2">
        <Table class="tw-bg-white tw-min-w-[900px] tw-w-full">
          <TableHeader>
            <TableRow>
              <TableHead
                @click="handleSort('order_number')"
                class="tw-cursor-pointer hover:tw-bg-gray-50 tw-text-xs tw-font-semibold tw-text-secondary-500 tw-uppercase tw-tracking-wider"
              >
                <div class="tw-flex tw-items-center tw-gap-1">
                  {{ t('orders.titles.order_no') }}
                  <span v-if="sortBy === 'order_number_without_prefix'" class="tw-text-xs">
                    {{ sortType === 'asc' ? '↑' : '↓' }}
                  </span>
                </div>
              </TableHead>
              <TableHead
                @click="handleSort('supplier_name')"
                class="tw-cursor-pointer hover:tw-bg-gray-50 tw-text-xs tw-font-semibold tw-text-secondary-500 tw-uppercase tw-tracking-wider"
              >
                <div class="tw-flex tw-items-center tw-gap-1">
                  {{ t('orders.titles.supplier') }}
                  <span v-if="sortBy === 'supplier_name'" class="tw-text-xs">
                    {{ sortType === 'asc' ? '↑' : '↓' }}
                  </span>
                </div>
              </TableHead>
              <TableHead
                class="tw-text-xs tw-font-semibold tw-text-secondary-500 tw-uppercase tw-tracking-wider"
              >
                {{ t('orders.titles.doc_no_internal') }}
              </TableHead>
              <TableHead
                class="tw-text-xs tw-font-semibold tw-text-secondary-500 tw-uppercase tw-tracking-wider"
              >
                {{ t('orders.titles.service_date') }}
              </TableHead>
              <TableHead
                class="tw-text-xs tw-font-semibold tw-text-secondary-500 tw-uppercase tw-tracking-wider"
              >
                {{ t('orders.titles.amount') }}
              </TableHead>
              <TableHead
                class="tw-text-xs tw-font-semibold tw-text-secondary-500 tw-uppercase tw-tracking-wider"
              >
                {{ t('payment.titles.balance') }}
              </TableHead>
              <TableHead
                class="tw-w-[200px] tw-text-xs tw-font-semibold tw-text-secondary-500 tw-uppercase tw-tracking-wider"
              >
                {{ t('payment.titles.allocated_amount') }}
              </TableHead>
              <TableHead
                class="tw-text-xs tw-font-semibold tw-text-secondary-500 tw-uppercase tw-tracking-wider"
              >
                {{ t('payment.titles.new_balance') }}
              </TableHead>
              <TableHead
                class="tw-text-xs tw-font-semibold tw-text-secondary-500 tw-uppercase tw-tracking-wider"
              >
                {{ t('payment.titles.new_amount') }}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <template v-if="orderList.length > 0">
              <TableRow
                v-for="(order, index) in sortedOrders"
                :key="order.id"
                :class="index % 2 === 1 ? 'tw-bg-gray-50' : ''"
              >
                <TableCell>{{ order.order_number }}</TableCell>
                <TableCell>{{ order.supplier_name }}</TableCell>
                <TableCell>{{ order.document_no_internal }}</TableCell>
                <TableCell>{{ moment(order.service_date).format('DD.MM.YYYY') }}</TableCell>
                <TableCell>
                  <div class="tw-flex tw-items-center tw-gap-3">
                    {{ order.amount ? n(+order.amount, 'currency') : n(0, 'currency') }}
                  </div>
                </TableCell>
                <TableCell>
                  <div class="tw-flex tw-items-center tw-gap-3">
                    {{
                      order.current_balance
                        ? n(+order.current_balance, 'currency')
                        : n(0, 'currency')
                    }}
                  </div>
                </TableCell>
                <TableCell>
                  <CurrencyInput
                    v-model.number="order.allocated_amount"
                    class="tw-flex tw-h-9 tw-w-full tw-rounded-md tw-border tw-border-neutral-200 tw-bg-transparent tw-px-3 tw-py-1 tw-text-sm"
                    :placeholder="t('payment.titles.amount_to_allocate')"
                  />
                </TableCell>
                <TableCell>
                  <div class="tw-flex tw-items-center tw-gap-3">
                    {{ order.new_balance ? n(+order.new_balance, 'currency') : n(0, 'currency') }}
                  </div>
                </TableCell>
                <TableCell>
                  {{ order.new_amount ? n(+order.new_amount, 'currency') : n(0, 'currency') }}
                </TableCell>
              </TableRow>
            </template>
            <TableEmpty v-else :colspan="9">
              {{ t('common.data.no_data') }}
            </TableEmpty>
          </TableBody>
        </Table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import moment from 'moment';
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CurrencyInput from '@/components/shared/currency-input/CurrencyInput.vue';
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const props = defineProps({
  paymentId: {
    default: null,
    type: [Number, String],
  },
  paymentAmount: {
    default: 0,
    type: Number,
  },
  list: {
    default: () => [],
    type: Array,
  },
  excessAmount: {
    default: 0,
    type: Number,
  },
});

const { t, n } = useI18n();

const loading = ref(false);

// Sorting state
const sortBy = ref('order_number_without_prefix');
const sortType = ref<'asc' | 'desc'>('asc');

const orderList = ref<any[]>([]);

// Computed property for sorted orders
const sortedOrders = computed(() => {
  let sorted = [...orderList.value];

  if (sortBy.value) {
    sorted.sort((a, b) => {
      let aVal = a[sortBy.value === 'order_number_without_prefix' ? 'order_number' : sortBy.value];
      let bVal = b[sortBy.value === 'order_number_without_prefix' ? 'order_number' : sortBy.value];

      if (typeof aVal === 'string') {
        aVal = aVal?.toLowerCase() || '';
        bVal = bVal?.toLowerCase() || '';
      }

      if (sortType.value === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  }

  return sorted;
});

const handleSort = (column: string) => {
  const sortColumn = column === 'order_number' ? 'order_number_without_prefix' : column;

  if (sortBy.value === sortColumn) {
    sortType.value = sortType.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortBy.value = sortColumn;
    sortType.value = 'asc';
  }
};

onMounted(() => {
  unallocatedAmount.value = props.paymentAmount;
  orderList.value = props.list?.map((item: any) => {
    return {
      ...item,
      allocated_amount: 0,
      new_balance: item.current_balance,
      new_amount: Number(item.amount),
      amount: Number(item.amount),
    };
  });

  if (props.list.length == 1) {
    orderList.value[0].allocated_amount = props.paymentAmount;
  }
  onAdjustment();
});

const emit = defineEmits(['adjusted']);

const unallocatedAmount = ref(0);
const unAllocatedAmountCents = ref(0);

watch(
  () => orderList.value,
  () => {
    onAdjustment();
  },
  { deep: true }
);

const onAdjustment = () => {
  let totalAllocatedAmountCents = 0;
  orderList.value.forEach((item: any) => {
    let itemAllocatedAmountCents = Math.round(item.allocated_amount * 100);
    totalAllocatedAmountCents += itemAllocatedAmountCents;
    let newBalanceCents = Math.round(item.balance * 100) - itemAllocatedAmountCents;

    if (newBalanceCents <= 0) {
      item.new_balance = 0;
      item.new_amount =
        item.amount + (itemAllocatedAmountCents - Math.round(item.balance * 100)) / 100;
    } else {
      item.new_balance = newBalanceCents / 100;
      item.new_amount = item.amount;
    }
  });
  unAllocatedAmountCents.value = Math.round(props.paymentAmount * 100) - totalAllocatedAmountCents;
  unallocatedAmount.value = unAllocatedAmountCents.value / 100;

  emit('adjusted', {
    orders: orderList.value.map((item: any) => {
      return {
        id: item.id,
        allocated_amount: item.allocated_amount,
      };
    }),
    paymentId: props.paymentId,
    isValid: unAllocatedAmountCents.value === 0,
  });
};

const distributeEvenly = () => {
  if (!orderList.value.length) return;

  // Work with cents to avoid floating point issues
  const totalCents = Math.round(props.paymentAmount * 100);
  const orderCount = orderList.value.length;
  const baseAmountCents = Math.floor(totalCents / orderCount);
  const remainderCents = totalCents - baseAmountCents * orderCount;

  orderList.value.forEach((order: any, index: number) => {
    // Add 1 cent to the first 'remainderCents' orders to distribute the remainder
    const allocatedCents = index < remainderCents ? baseAmountCents + 1 : baseAmountCents;
    order.allocated_amount = allocatedCents / 100;
  });

  onAdjustment();
};
</script>

<style scoped>
/* Custom scrollbar styles for better UX */
.tw-overflow-x-auto {
  -webkit-overflow-scrolling: touch;
}

.tw-overflow-x-auto::-webkit-scrollbar {
  height: 10px;
}

.tw-overflow-x-auto::-webkit-scrollbar-track {
  @apply tw-bg-gray-100 tw-rounded;
}

.tw-overflow-x-auto::-webkit-scrollbar-thumb {
  @apply tw-bg-gray-400 tw-rounded hover:tw-bg-gray-500;
}
</style>
